// src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Container,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    CircularProgress,
    Box,
    CssBaseline,
    ThemeProvider,
    createTheme,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Divider,
    Avatar,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    Popover,
    Tooltip,
    useMediaQuery,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CloudIcon from '@mui/icons-material/Cloud';
import DevicesIcon from '@mui/icons-material/Devices';
import TranslateIcon from '@mui/icons-material/Translate';
import MicIcon from '@mui/icons-material/Mic';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AbcIcon from '@mui/icons-material/Abc';
import ShieldAlertOutlineIcon from '@mui-extra/icons/ShieldAlertOutlineIcon';
import AccountMultipleOutlineIcon from '@mui-extra/icons/AccountMultipleOutlineIcon';
import CommaCircleOutlineIcon from '@mui-extra/icons/CommaCircleOutlineIcon';
import ProfessionalHexagonIcon from '@mui-extra/icons/ProfessionalHexagonIcon';
import TimerCancelOutlineIcon from '@mui-extra/icons/TimerCancelOutlineIcon';
import TimerLockOpenOutlineIcon from '@mui-extra/icons/TimerLockOpenOutlineIcon';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// A modern, clean theme for the app
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f4f6f8',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.12)',
                    },
                },
            },
        },
    },
});

const ITEMS_PER_PAGE = 48;

function App() {
    const [languages, setLanguages] = useState([]);
    const [filteredLanguages, setFilteredLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [vendorFilter, setVendorFilter] = useState('all');
    const [engineFilter, setEngineFilter] = useState('all');
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
    const [popoverContent, setPopoverContent] = useState('');
    const [popoverPosition, setPopoverPosition] = useState(null);

    const isHoverDevice = useMediaQuery('(hover: hover)');

    const handlePopoverOpen = (event, content) => {
        event.stopPropagation();
        setPopoverAnchorEl(null);
        setPopoverPosition({ top: event.clientY, left: event.clientX });
        setPopoverContent(content);

        setTimeout(() => {
            handlePopoverClose();
        }, 5000);
    };

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null);
        setPopoverPosition(null);
        setPopoverContent('');
    };

    const openPopover = Boolean(popoverAnchorEl) || Boolean(popoverPosition);

    const handleOpenModal = (lang) => setSelectedLanguage(lang);
    const handleCloseModal = () => setSelectedLanguage(null);

    const observer = useRef();
    const lastLanguageElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleCount < filteredLanguages.length) {
                setVisibleCount(prev => prev + ITEMS_PER_PAGE);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, visibleCount, filteredLanguages.length]);

    useEffect(() => {
        const apiUrl = process.env.NODE_ENV === 'development'
            ? 'http://localhost:5000/api/languages'
            : '/api/languages';

        const fetchLanguages = async () => {
            try {
                const response = await axios.get(apiUrl);
                const sortedData = response.data.sort((a, b) => a.displayName.localeCompare(b.displayName));
                setLanguages(sortedData);
                setFilteredLanguages(sortedData);
            } catch (err) {
                setError('Failed to fetch language data. Is the proxy server running?');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchLanguages();
    }, []);

    useEffect(() => {
        let result = languages;

        if (vendorFilter !== 'all') {
            result = result.filter(lang => lang.vendors[vendorFilter]);
        }

        if (engineFilter !== 'all') {
            result = result.filter(lang => Object.values(lang.vendors).some(v => v.engines && v.engines.some(e => e.kind === engineFilter)));
        }

        if (searchTerm) {
            result = result.filter(lang =>
                lang.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                lang.bcp47Code.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredLanguages(result);
        setVisibleCount(ITEMS_PER_PAGE);
    }, [searchTerm, vendorFilter, engineFilter, languages]);

    const capitalize = (s) => {
        if (s === 'aws') return 'AWS';
        if (s === 'cloudxrai') return 'XRAI';
        if (s === 'sensevoice') return 'SenseVoice';
        if (s === 'nvidia-nemo') return 'NVIDIA NeMo';
        if (s === 'ct2-small100') return 'SMaLL-100';
        if (s === 'gguf-madlad') return 'MADLAD-400';
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    const vendors = ['all', ...Array.from(new Set(languages.flatMap(lang => Object.keys(lang.vendors))))
        .sort((a, b) => capitalize(a).localeCompare(capitalize(b)))];

    const InfoIconWrapper = ({ content, children }) => {
        if (isHoverDevice) {
            return <Tooltip title={content} componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}><Box component="span" sx={{ '&:hover': { color: 'common.black' }, display: 'flex', alignItems: 'center' }}>{children}</Box></Tooltip>;
        }
        return <Box component="span" sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={(e) => handlePopoverOpen(e, content)}>{children}</Box>;
    };

    const renderModels = (models) => {
        return (
            <Grid container spacing={2}>
                {models.map(model => (
                    <Grid item xs={12} sm={6} key={`${model.vendorName}-${model.modelName}`}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <InfoIconWrapper content={capitalize(model.vendorName)}>
                                    <img
                                        src={`/language-finder/images/${model.vendorName}.png`}
                                        alt={`${model.vendorName} logo`}
                                        style={{ width: 20, height: 20, marginRight: 8 }}
                                    />
                                </InfoIconWrapper>
                                <Typography variant="body2">
                                    <strong>{model.friendlyName}</strong>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                                    Supports:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, color: 'grey.500' }}>
                                    {model.capabilities.diarization && <InfoIconWrapper content="Speaker Identification"><AccountMultipleOutlineIcon fontSize="small" /></InfoIconWrapper>}
                                    {model.capabilities.punctuation && <InfoIconWrapper content="Punctuation"><CommaCircleOutlineIcon fontSize="small" /></InfoIconWrapper>}
                                    {model.capabilities.profanityFilter && <InfoIconWrapper content="Profanity Filter"><ShieldAlertOutlineIcon fontSize="small" /></InfoIconWrapper>}
                                    {model.capabilities.noiseReduction && <InfoIconWrapper content="Noise Reduction"><GraphicEqIcon fontSize="small" /></InfoIconWrapper>}
                                    {model.capabilities.translate && <InfoIconWrapper content="Supports output in target language"><AbcIcon fontSize="small" /></InfoIconWrapper>}
                                    {model.capabilities.automaticLanguageDetection && <InfoIconWrapper content="Automatic Language Detection"><AutoAwesomeIcon fontSize="small" /></InfoIconWrapper>}
                                </Box>
                                {model.vendorName === 'google' && model.regions && model.regions.length > 0 && (
                                    <Box sx={{ ml: 'auto' }}>
                                        <InfoIconWrapper content={`Available in:\n${model.regions.sort().join('\n')}`}>
                                            <InfoOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InfoIconWrapper>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const getCountryCode = (bcp47) => {
        const parts = bcp47.split('-');
        if (parts.length > 1) {
            const regionPart = parts.slice(1).find(part => part.length === 2);
            if (regionPart && regionPart.toLowerCase() !== 'xa') {
                return regionPart.toLowerCase();
            }
        }

        const languageMap = {
            'ar': 'sa', 'xa': 'sa',
            'cgg': 'ug', 'ab': 'ge', 'ban': 'id', 'bew': 'id', 'bem': 'zm', 'bbc': 'id',
            'awa': 'in', 'ace': 'id', 'brx': 'in', 'ay': 'bo', 'bho': 'in',
            'bik': 'ph', 'ach': 'ug', 'btx': 'id', 'dov': 'ao', 'doi': 'in', 'bua': 'ru',
            'din': 'ss', 'dv': 'mv', 'bts': 'id', 'crh': 'ua', 'cnh': 'mm', 'hrx': 'br',
            'hil': 'ph', 'gom': 'in', 'jw': 'id', 'ilo': 'ph', 'haw': 'us', 'hmn': 'cn',
            'gaa': 'gh', 'pap': 'aw', 'crs': 'sc', 'szl': 'pl', 'lus': 'in', 'lzh': 'cn', 
            'zh': 'cn', 'prs': 'af', 'hne': 'in', 'iu': 'ca', 'lug': 'ug', 'ks': 'in', 
            'kri': 'sl', 'ktu': 'cg', 'tlh': 'us', 'lij': 'it', 'dsb': 'de', 'min': 'id',
            'lmo': 'it', 'ku': 'iq', 'mak': 'id', 'mni': 'in', 'ltg': 'lv', 'kmr': 'tr',
            'mai': 'np', 'chm': 'ru', 'run': 'bi', 'rom': 'ro', 'pag': 'ph', 'qu': 'pe',
            'pam': 'ph', 'shn': 'mm', 'mww': 'in', 'nus': 'ss', 'rn': 'bi', 'new': 'np',
            'otq': 'mx', 'nya': 'mw', 'scn': 'it', 'ty': 'pf', 'tet': 'tl', 'sw': 'ke',
            'hsb': 'de', 'ti': 'er', 'yua': 'mx', 'yi': 'il', 'fy': 'nl', 'ikt': 'ca'
        };
        const langPart = bcp47.split('-')[0].toLowerCase();
        if (languageMap[langPart]) {
            return languageMap[langPart];
        }

        return parts[0].toLowerCase();
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md>
                            <TextField
                                label="Search"
                                variant="outlined"
                                fullWidth
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {searchTerm && (
                                                <IconButton
                                                    aria-label="clear search"
                                                    onClick={() => setSearchTerm('')}
                                                    edge="end"
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Vendor</InputLabel>
                                <Select
                                    value={vendorFilter}
                                    onChange={e => setVendorFilter(e.target.value)}
                                    label="Vendor"
                                >
                                    {vendors.map(vendor => (
                                        <MenuItem key={vendor} value={vendor}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {vendor !== 'all' && (
                                                    <img
                                                        src={`/language-finder/images/${vendor}.png`}
                                                        alt={`${vendor} logo`}
                                                        style={{ width: 20, height: 20, marginRight: 10 }}
                                                    />
                                                )}
                                                {capitalize(vendor)}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={5} md="auto">
                            <Box sx={{
                                position: 'relative',
                                height: '56px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                borderRadius: 1,
                                '&:hover': {
                                    borderColor: 'text.primary',
                                },
                                padding: '0 14px',
                                boxSizing: 'border-box',
                            }}>
                                <Typography
                                    component="label"
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        position: 'absolute',
                                        top: 0,
                                        left: '10px',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'background.paper',
                                        px: '4px',
                                    }}
                                >
                                    Service
                                </Typography>
                                <ToggleButtonGroup
                                    value={engineFilter}
                                    exclusive
                                    size="small"
                                    onChange={(e, newValue) => setEngineFilter(newValue || 'all')}
                                    aria-label="engine type"
                                >
                                    <ToggleButton value="stt" aria-label="speech to text">
                                        <MicIcon />
                                    </ToggleButton>
                                    <ToggleButton value="translator" aria-label="translation">
                                        <TranslateIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3} md={2} sx={{ textAlign: { sm: 'right' } }}>
                            <Typography variant="h6" component="div">
                                {filteredLanguages.length} / {languages.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Languages Found
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Typography color="error" align="center">
                        {error}
                    </Typography>
                )}

                {!loading && !error && (
                    <Grid container spacing={3} alignItems="flex-start">
                        {filteredLanguages.slice(0, visibleCount).map((lang, index) => {
                            const countryCode = getCountryCode(lang.bcp47Code);

                            let displayName = lang.displayName;
                            let dialect = '';
                            const dialectMatch = displayName.match(/\(([^)]+)\)/);
                            if (dialectMatch) {
                                dialect = dialectMatch[1];
                                displayName = displayName.replace(/\s*\(([^)]+)\)/, '').trim();
                            }

                            const LanguageName = () => (
                                <Typography variant="h6" component="div">
                                    {displayName}
                                </Typography>
                            );

                            const sttVendors = Object.entries(lang.vendors).filter(([,v]) => v.engines && v.engines.some(e => e.kind === 'stt')).map(([name]) => name);
                            const translatorVendors = Object.entries(lang.vendors).filter(([,v]) => v.engines && v.engines.some(e => e.kind === 'translator')).map(([name]) => name);

                            return (
                                <Grid item xs={12} sm={6} md={4} key={lang.bcp47Code}
                                    ref={index === visibleCount - 1 ? lastLanguageElementRef : null}>
                                    <Card sx={{ minWidth: 270, height: '100%' }}>
                                        <CardActionArea onClick={() => handleOpenModal(lang)} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <CardContent sx={{ width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Avatar
                                                        src={`https://cdn.xrai.glass/share/images/flags/${countryCode}.svg`}
                                                        sx={{ width: 40, height: 40, mr: 2, flexShrink: 0 }}
                                                    >
                                                        {countryCode.toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        {dialect ? (
                                                            <InfoIconWrapper content={dialect}>
                                                                <LanguageName />
                                                            </InfoIconWrapper>
                                                        ) : (
                                                            <LanguageName />
                                                        )}
                                                        <Typography color="text.secondary">
                                                            {lang.bcp47Code}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1, color: 'text.secondary' }}>
                                                        {sttVendors.length > 0 && (
                                                            <InfoIconWrapper content="Supports Speech-to-Text (audio to text)">
                                                                <MicIcon />
                                                            </InfoIconWrapper>
                                                        )}
                                                        {translatorVendors.length > 0 && (
                                                            <InfoIconWrapper content="Supports Translation (text to this language)">
                                                                <TranslateIcon />
                                                            </InfoIconWrapper>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', gap: 3, rowGap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                                                        {sttVendors.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                                    Speech-to-Text
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 0.5 }}>
                                                                    {sttVendors.map(vendor => (
                                                                        <InfoIconWrapper key={vendor} content={capitalize(vendor)}>
                                                                            <img
                                                                                src={`/language-finder/images/${vendor}.png`}
                                                                                alt={`${vendor} logo`}
                                                                                style={{ width: 24, height: 24, marginRight: 8 }}
                                                                            />
                                                                        </InfoIconWrapper>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        {translatorVendors.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                                    Translation
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 0.5 }}>
                                                                    {translatorVendors.map(vendor => (
                                                                        <InfoIconWrapper key={vendor} content={capitalize(vendor)}>
                                                                            <img
                                                                                src={`/language-finder/images/${vendor}.png`}
                                                                                alt={`${vendor} logo`}
                                                                                style={{ width: 24, height: 24, marginRight: 8 }}
                                                                            />
                                                                        </InfoIconWrapper>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <OpenInNewIcon fontSize="small" color="action" />
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                )}

                {loading && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                )}

                {selectedLanguage && (() => {
                    const flattenEngines = (vendors, kind, location) => {
                        const engines = [];
                        for (const [vendorName, vendorDetails] of Object.entries(vendors)) {
                            if (vendorDetails.engines) {
                                for (const engine of vendorDetails.engines) {
                                    if (engine.kind === kind && engine.location === location) {
                                        for (const [modelName, modelDetails] of Object.entries(engine.models)) {
                                            engines.push({ vendorName, modelName, ...modelDetails });
                                        }
                                    }
                                }
                            }
                        }
                        return engines.sort((a, b) => a.vendorName.localeCompare(b.vendorName) || a.friendlyName.localeCompare(b.friendlyName));
                    };

                    const sttCloudModels = flattenEngines(selectedLanguage.vendors, 'stt', 'cloud');
                    const sttMobileModels = flattenEngines(selectedLanguage.vendors, 'stt', 'on-device-mobile');
                    const sttDesktopModels = flattenEngines(selectedLanguage.vendors, 'stt', 'on-device-desktop');
                    const translatorModels = flattenEngines(selectedLanguage.vendors, 'translator', 'cloud');
                    const translatorMobileModels = flattenEngines(selectedLanguage.vendors, 'translator', 'on-device-mobile');
                    const translatorDesktopModels = flattenEngines(selectedLanguage.vendors, 'translator', 'on-device-desktop');
                    const countryCode = getCountryCode(selectedLanguage.bcp47Code);

                    const hasMobile = sttMobileModels.length > 0 || translatorMobileModels.length > 0;
                    const hasDesktop = sttDesktopModels.length > 0 || translatorDesktopModels.length > 0;
                    const hasCloud = sttCloudModels.length > 0 || translatorModels.length > 0;
                    const hasOnDevice = hasMobile || hasDesktop;
                    const modalMaxWidth = hasCloud && hasOnDevice ? 'md' : 'sm';
                    const gridWidth = hasCloud && hasOnDevice ? 6 : 12;

                    return (
                        <Dialog open={!!selectedLanguage} onClose={handleCloseModal} fullWidth maxWidth={modalMaxWidth}>
                            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'grey.50' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        src={`https://cdn.xrai.glass/share/images/flags/${countryCode}.svg`}
                                        sx={{ width: 40, height: 40, mr: 2, border: '1px solid #eee' }}
                                    />
                                    <Box>
                                        <Typography variant="h6">
                                            {selectedLanguage.displayName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedLanguage.bcp47Code}
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    aria-label="close"
                                    onClick={handleCloseModal}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    {hasCloud && (
                                        <Grid item xs={12} md={gridWidth}>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                                                <CloudIcon /> Cloud
                                                <InfoIconWrapper content="Requires subscription"><ProfessionalHexagonIcon /></InfoIconWrapper>
                                                <InfoIconWrapper content="Requires pro hours"><TimerCancelOutlineIcon /></InfoIconWrapper>
                                            </Typography>
                                            {sttCloudModels.length > 0 && (
                                                <Box sx={{ pl: 2, mb: 1 }}>
                                                    <Typography variant="subtitle1" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <MicIcon /> Speech-to-Text
                                                    </Typography>
                                                    {renderModels(sttCloudModels)}
                                                </Box>
                                            )}
                                            {translatorModels.length > 0 && (
                                                <Box sx={{ pl: 2, mt: 2 }}>
                                                    <Typography variant="subtitle1" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TranslateIcon /> Translation
                                                    </Typography>
                                                    {renderModels(translatorModels)}
                                                </Box>
                                            )}
                                        </Grid>
                                    )}
                                    {hasOnDevice && (
                                        <Grid item xs={12} md={gridWidth}>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'green' }}>
                                                <DevicesIcon /> On-Device
                                                <InfoIconWrapper content="Unlimited free usage"><TimerLockOpenOutlineIcon /></InfoIconWrapper>
                                            </Typography>
                                            {hasMobile && (
                                                <Box sx={{ pl: 2, mb: 1 }}>
                                                    <Typography variant="subtitle1" color="green" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <SmartphoneIcon /> Phones & Tablets
                                                    </Typography>
                                                    {sttMobileModels.length > 0 && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <MicIcon fontSize="inherit" /> Speech-to-Text
                                                            </Typography>
                                                            {renderModels(sttMobileModels)}
                                                        </Box>
                                                    )}
                                                    {translatorMobileModels.length > 0 && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <TranslateIcon fontSize="inherit" /> Translation
                                                            </Typography>
                                                            {renderModels(translatorMobileModels)}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                            {hasDesktop && (
                                                <Box sx={{ pl: 2, mt: 2 }}>
                                                    <Typography variant="subtitle1" color="green" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <DesktopMacOutlinedIcon /> Desktops & Servers
                                                    </Typography>
                                                    {sttDesktopModels.length > 0 && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <MicIcon fontSize="inherit" /> Speech-to-Text
                                                            </Typography>
                                                            {renderModels(sttDesktopModels)}
                                                        </Box>
                                                    )}
                                                    {translatorDesktopModels.length > 0 && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <TranslateIcon fontSize="inherit" /> Translation
                                                            </Typography>
                                                            {renderModels(translatorDesktopModels)}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>
                        </Dialog>
                    )
                })()}
                <Popover
                    open={openPopover}
                    anchorEl={popoverAnchorEl}
                    onClose={handlePopoverClose}
                    anchorReference={popoverPosition ? 'anchorPosition' : 'anchorEl'}
                    anchorPosition={popoverPosition}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: { p: 1.5, border: '1px solid #ddd', boxShadow: 'none', maxWidth: '300px' }
                    }}
                >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{popoverContent}</Typography>
                </Popover>
            </Container>
        </ThemeProvider>
    );
}

export default App;
